import clsx from 'clsx'
import type { RouterLocation } from 'connected-react-router'
import { replace } from 'connected-react-router'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { IntersectionOptions } from 'react-intersection-observer'
import { useInView, InView } from 'react-intersection-observer'
import { connect } from 'react-redux'
import styled from 'styled-components'

import Heading from 'LuxKit/Typography/Heading'
import { clearBookingCalendar, fetchCalendars } from 'actions/CalendarActions'
import { fetchAvailableRatesForOffer, fetchRelatedTravelItems } from 'actions/OfferActions'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import Pane from 'components/Common/Pane/Pane'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import UserReviewList from 'components/OfferPage/NavigableOfferContent/UserReviewList'
import OfferPageGallery from 'components/OfferPage/OfferPageGallery'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import { VillasContentSections } from 'constants/villas'
import ModalProvider from 'contexts/ModalProvider'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import type {
  OfferPageDispatchAction,
  OfferPageFilterType,
  OfferPageRoom,
  OfferPageState,
} from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  generateOfferPageRoom,
  initialOfferPageRooms,
} from 'contexts/OfferPage/offerPageStateReducer'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useMountedEffect from 'hooks/useMountedEffect'
import useShareModalConfig from 'hooks/useShareModalConfig'
import { nonNullable } from 'lib/array/arrayUtils'
import { occupantsFitAnyRoomCapacity } from 'lib/offer/occupancyUtils'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import type { ReducerMiddleware } from 'lib/redux/reducerUtils'
import { encodeSearchParams, isRoomsInvalid } from 'lib/search/searchUtils'
import { getInitialLeOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import variables from 'styles/vendor/bootstrap/variables'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { useSyncHomeAndVillasTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHomeAndVillaTripItem'

import VillasPageHeader from './VillasPageHeader'
import VillasPageIntro from './VillasPageIntro'
import VillasPageLocationMeta from './VillasPageLocationMeta'
import VillasPageRoomsAndAmenities from './VillasPageRoomsAndAmenities'
import VillasPageSidebarFilters from './VillasPageSidebarFilters'
import VillasPageStickyFooter from './VillasPageStickyFooter'
import VillasPageStickyHeader from './VillasPageStickyHeader'
import VillasSummaryPanel from './VillasSummaryPanel'
import VillasThingsToKnow from './VillasThingsToKnow'
import VillasWhyBookWithUs from './VillasWhyBookWithUs'

const CONTAINER_PADDING = rem(40)

const StickySummaryContainer = styled(CSSBreakpoint)`
  position: sticky;
  top: 0;
  z-index: 2;
  align-self: flex-start;
  transition: top 0.2s;
  margin: ${CONTAINER_PADDING} 0;

  &.has-header {
    top: ${rem(92)};
  }
`

const Section = styled(Pane)`
  padding: ${CONTAINER_PADDING} 0;
`

const SectionDivider = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  height: ${rem(12)};
  /* We're using negative margins to offset LayoutContainer's padding. */
  margin: 0 -${rem(variables.gutterWidth / 2)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const ContentGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto;
  column-gap: ${rem(32)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: minmax(0, 1fr) ${rem(400)};
  }
`

function getNextFilterMiddlware(
  state: OfferPageState,
  action: OfferPageDispatchAction,
  next: (state: OfferPageState) => OfferPageState,
) {
  if (action.type === OfferPageActions.filterContinue) {
    const hasRooms = hasSelectedRooms(state.rooms)
    const hasDate = !!state.checkInDate
    const hasInvalidChildren = state.rooms?.some((room) =>
      room.childrenAge?.some((age) => age === -1),
    )
    /*
      Base filter flow is: Capacity -> Duration -> Check in
      Then there's a priority list based on what data is missing
    */
    const nextState = next(state)
    let nextFilter: OfferPageFilterType | undefined = undefined
    if (hasInvalidChildren) {
      // invalid children, don't let them progress until dealth with
      nextFilter = 'capacity'
    } else if (state.activeFilter === 'duration') {
      nextFilter = 'checkin'
    } else if (state.activeFilter === 'capacity') {
      if (!hasRooms) {
        // user looked at capacity, didn't change anything and closed it
        // we consider this them applying the default rooms, so set rooms
        nextState.rooms = [generateOfferPageRoom()]
      }
      nextFilter = 'duration'
    } else if (state.activeFilter === 'checkin') {
      if (!hasRooms && hasDate) {
        nextFilter = 'capacity'
      }
    }
    nextState.activeFilter = nextFilter
    return nextState
  }

  return state
}

const middlewares: Array<ReducerMiddleware<OfferPageState, OfferPageDispatchAction>> = [getNextFilterMiddlware]

type CartDefaults = Partial<OfferPageState>

interface Props {
  offer: App.VillaOffer
}

interface MappedProps {
  defaults: CartDefaults
  routerLocation: RouterLocation<unknown>
  headlessMode: boolean
}

const inViewOptions: IntersectionOptions = { delay: 100, rootMargin: '-85px 0px 0px 0px' }

function VillasPageContent({
  defaults,
  offer,
  routerLocation,
  headlessMode,
}: MappedProps & Props) {
  const intl = useIntl()
  const { id, packages } = offer

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!defaults.checkInDate)
  const [state, stateDispatch] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
    // Reduce rooms to a single room
    rooms: defaults.rooms ? defaults.rooms.reduce((acc, room) => {
      acc[0].adults += room.adults
      acc[0].children += room.children ?? 0
      if (room.childrenAge) {
        acc[0].childrenAge = acc[0].childrenAge.concat(room.childrenAge)
      }
      return acc
    }, [{ roomId: defaults.rooms[0].id, adults: 0, children: 0, childrenAge: [] as Array<number> }]) as Array<OfferPageRoom> : initialOfferPageRooms,
  }, middlewares)
  const dispatch = useAppDispatch()

  const bestPackage = useAppSelector(appState => getCheapestSelectedPackageOptionWithDefault(appState, state))
  const bypassRegionFilter = useAppSelector(state => state.marketing.bypassRegionFilter)
  const { pricing, pricingWithFlights } = bestPackage
  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent && !offer.bundledWithFlightsOnly) || (offer.bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  const loggedOutSavedPercent = showDiscountPill ? offer.bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : pricing.discountPercent : undefined

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  const {
    activeFilter,
    checkInDate,
    checkOutDate,
    duration,
    rooms = initialOfferPageRooms,
  } = state

  const lastFetchedRooms = useRef<Array<OfferPageRoom> | undefined>(undefined)

  const [overviewRef, isOverviewVisible, overviewEntry] = useInView(inViewOptions)
  const [roomsAndAmenitiesRef, isRoomsAndAmenitiesVisible, roomsAndAmenitiesEntry] = useInView(inViewOptions)
  const [locationRef, isLocationVisible, locationEntry] = useInView(inViewOptions)
  const [reviewsRef, isReviewsVisible, reviewsEntry] = useInView(inViewOptions)
  const [thingsToKnowRef, isThingsToKnowVisible, thingsToKnowEntry] = useInView(inViewOptions)
  const [whyBookWithUsRef, isWhyBookWithUsVisible, whyBookWithUsEntry] = useInView(inViewOptions)

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: routerLocation.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
    }).toString(), [checkInDate, checkOutDate, rooms, routerLocation.search])

  const visibleSection = useMemo(() => {
    const values = [
      isOverviewVisible ? VillasContentSections.OVERVIEW : undefined,
      isRoomsAndAmenitiesVisible ? VillasContentSections.ROOMS_AND_AMENITIES : undefined,
      isLocationVisible ? VillasContentSections.LOCATION : undefined,
      isReviewsVisible ? VillasContentSections.REVIEWS : undefined,
      isThingsToKnowVisible ? VillasContentSections.THINGS_TO_KNOW : undefined,
      isWhyBookWithUsVisible ? VillasContentSections.WHY_BOOK_WITH_US : undefined,
    ]

    return nonNullable(values)[0]
  }, [
    isOverviewVisible,
    isRoomsAndAmenitiesVisible,
    isLocationVisible,
    isReviewsVisible,
    isThingsToKnowVisible,
    isWhyBookWithUsVisible,
  ])

  const scrollToSection = useCallback(
    (section: VillasContentSections) => {
      const sectionRefs: Record<VillasContentSections, IntersectionObserverEntry | undefined> = {
        [VillasContentSections.OVERVIEW]: overviewEntry,
        [VillasContentSections.ROOMS_AND_AMENITIES]: roomsAndAmenitiesEntry,
        [VillasContentSections.LOCATION]: locationEntry,
        [VillasContentSections.REVIEWS]: reviewsEntry,
        [VillasContentSections.THINGS_TO_KNOW]: thingsToKnowEntry,
        [VillasContentSections.WHY_BOOK_WITH_US]: whyBookWithUsEntry,
      }

      sectionRefs[section]?.target.scrollIntoView({ block: 'start', inline: 'nearest' })
    },
    // Entries are stable refs
    [
      overviewEntry,
      roomsAndAmenitiesEntry,
      locationEntry,
      reviewsEntry,
      thingsToKnowEntry,
      whyBookWithUsEntry,
    ],
  )

  const fetchCalendarData = () => {
    // if we have any invalid ages, don't try fetch - it'll fail anyway
    if (!isRoomsInvalid(rooms)) {
      dispatch(
        fetchCalendars(id, {
          occupants: rooms,
          packages: [offer.lowestPricePackage!],
          bypassRegionFilter,
        }),
      )
      dispatch(
        fetchCalendars(id, {
          occupants: rooms,
          packages: packages.filter((p) => p.duration === duration),
          bypassRegionFilter,
        }),
      )

      lastFetchedRooms.current = rooms
    }
  }

  useSyncHomeAndVillasTripItem(state, offer)
  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  useEffect(() => {
    dispatch(fetchRelatedTravelItems(offer))
  }, [dispatch, offer])

  useEffect(() => {
    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else if (viewedDates && lastFetchedRooms.current !== rooms) {
      // rooms has changed, all loaded calendars are no longer valid as we load calendars for a specific
      // combinations occupants (availability)
      dispatch(clearBookingCalendar())
      fetchCalendarData()
    }
  }, [activeFilter]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && packages) {
      fetchCalendarData()
    }
    // eslint-disable-next-line
  }, [packages]);

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates && activeFilter === 'checkin') {
      fetchCalendarData()
    }
  }, [duration, activeFilter])

  useEffect(() => {
    const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
    const occupanciesInvalid = isRoomsInvalid(rooms)
    if (!!checkInDate && !!checkOutDate && !occupanciesInvalid && !activeFilter && useDynamicCancellationPolicies) {
      dispatch(fetchAvailableRatesForOffer(
        offer,
        {
          occupants: rooms,
          checkIn: checkInDate,
          checkOut: checkOutDate,
        },
      ))
    }
  }, [duration, activeFilter, checkInDate, checkOutDate, offer, dispatch, rooms])

  useEffect(() => {
    if (rooms === initialOfferPageRooms && activeFilter === 'capacity') {
      stateDispatch({ type: OfferPageActions.setRooms, rooms: [generateOfferPageRoom()] })
    }
  }, [activeFilter, rooms, stateDispatch])

  useEffect(() => {
    dispatch(replace({ ...routerLocation, search: queryString }))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  // Reset on invalid occupancies (e.g. when arriving from search).
  useEffect(() => {
    // Villas only support booking a single occupancy room. Multiple occupancy rooms have been
    // reduced by this point.
    const occupancy = rooms[0]
    // Villas are configured with only one package.
    const capacities = offer.packages[0]?.roomRate.capacities
    if (occupancy && capacities && !occupantsFitAnyRoomCapacity(occupancy, capacities, offer)) {
      stateDispatch({ type: OfferPageActions.setRooms, rooms: [generateOfferPageRoom()] })
    }
  }, [offer, rooms, stateDispatch])

  // Reset on invalid durations (e.g. when arriving from search).
  // NB: This is commented out because we're making this check before dynamic availability has
  // returned, leading to false positives based on static availability.
  // useEffect(() => {
  //   const hasValidDuration = offer.packages.some(pkg => pkg.duration === duration)
  //   if (!hasValidDuration) {
  //     stateDispatch({
  //       type: OfferPageActions.setDuration,
  //       duration: offer.lowestPricePackage?.duration ?? 1,
  //     })
  //   }
  // }, [duration, offer, stateDispatch])

  const [isBelowGallery, setIsBelowGallery] = useState<boolean>()

  const stickyBarVisible = isBelowGallery && !activeFilter

  const shareConfig = useShareModalConfig({
    type: 'offer',
    offerId: offer.id,
    image: offer.images[0],
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    offerType: 'hotel',
  })

  return (
    <OfferPageStateProvider
      state={state}
      dispatch={stateDispatch}
    >
      <TripGuard>
        <ImmersiveSaveButtonPortal>
          <BookmarkButton offer={offer} />
        </ImmersiveSaveButtonPortal>
      </TripGuard>
      <ModalProvider>
        <VillasPageSidebarFilters />
      </ModalProvider>
      <Pane>
        {!headlessMode && <VillasPageHeader offer={offer} />}
        <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
        <InView rootMargin="0px 0px 1000px 0px" onChange={(inView) => setIsBelowGallery(!inView)} />
        <VillasPageStickyHeader
          onTabClick={scrollToSection}
          visibleSection={visibleSection}
          visible={stickyBarVisible}
        />
        <LayoutContainer>
          <ContentGrid>
            <div>
              <Section ref={overviewRef}>
                <VillasPageIntro
                  offer={offer}
                  onViewLocationClick={() => scrollToSection(VillasContentSections.LOCATION)}
                  onViewReviewsClick={() => scrollToSection(VillasContentSections.REVIEWS)}
                />
              </Section>
              <SoftOfferPageWalledGarden>
                <SectionDivider />
                <Section ref={roomsAndAmenitiesRef}>
                  <VillasPageRoomsAndAmenities offer={offer} />
                </Section>
                <SectionDivider />
                <Section ref={locationRef}>
                  <VillasPageLocationMeta offer={offer} />
                </Section>
                {isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD) && <>
                  <SectionDivider />
                  <Section ref={reviewsRef}>
                    <Group direction="vertical" gap={32}>
                      <Heading variant="heading2">
                        {intl.formatMessage({
                          id: 'gDh6KQnsGqbAeWtPsgI9',
                          defaultMessage: 'Reviews',
                          description: 'Section title for villa property reviews on the rental page',
                        })}
                      </Heading>
                      <UserReviewList
                        property={offer.property}
                        showReviewFiltersAndUserDetails={false}
                        reviewsThreshold={VILLAS_REVIEWS_TOTAL_THRESHOLD}
                      />
                    </Group>
                  </Section>
                </>}
                <SectionDivider />
                <Section ref={thingsToKnowRef}>
                  <VillasThingsToKnow offer={offer} />
                </Section>
              </SoftOfferPageWalledGarden>
            </div>
            <StickySummaryContainer
              min="desktop"
              className={clsx('sticky-container', { 'has-header': stickyBarVisible })}
            >
              <VillasSummaryPanel />
            </StickySummaryContainer>
          </ContentGrid>
        </LayoutContainer>
        <SoftOfferPageWalledGarden>
          <Section paletteType="inverse" ref={whyBookWithUsRef}>
            <LayoutContainer>
              <VillasWhyBookWithUs />
            </LayoutContainer>
          </Section>
          <CSSBreakpoint max="tablet">
            <VillasPageStickyFooter />
          </CSSBreakpoint>
        </SoftOfferPageWalledGarden>
      </Pane>
      <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    </OfferPageStateProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Props) {
  return {
    defaults: getInitialLeOfferPageState(state, ownProps.offer),
    routerLocation: state.router.location,
    headlessMode: state.system.headlessMode,
  }
}

export default connect(mapStateToProps)(VillasPageContent)
